import axios from "axios";
import { showAlert, handleRequestError } from "../utils/alert";
import VMasker from "vanilla-masker";

/**
 * Validate CPF using official verification digit logic.
 *
 * @param {string} cpf
 * @returns {boolean}
 */
const isValidCPF = (cpf) => {
  const cleanCPF = cpf.replace(/\D/g, "");

  if (cleanCPF.length !== 11 || /^(\d)\1{10}$/.test(cleanCPF)) {
    return false;
  }

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(10, 11))) return false;

  return true;
};

/**
 * Validate CNPJ using official verification digit logic.
 *
 * @param {string} cnpj
 * @returns {boolean}
 */
const isValidCNPJ = (cnpj) => {
  const cleanCNPJ = cnpj.replace(/\D/g, "");

  if (cleanCNPJ.length !== 14 || /^(\d)\1{13}$/.test(cleanCNPJ)) {
    return false;
  }

  let size = cleanCNPJ.length - 2;
  let numbers = cleanCNPJ.substring(0, size);
  const digits = cleanCNPJ.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cleanCNPJ.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
};

export default (initialPlan, initialCycle) => ({
  plan_slug: initialPlan,
  cycle: initialCycle,
  billing_type: "PIX", // Defaults to PIX to promote higher conversion rates
  document: "",
  company_name: "",
  name: "",
  email: "",
  phone: "",
  password: "",
  password_confirmation: "",
  isLoading: false,
  errors: {},

  /**
   * Dynamic input handler that evaluates pure digit length to toggle between CPF and CNPJ templates.
   *
   * @param {Event} event
   */
  handleDocumentInput(event) {
    const rawValue = event.target.value;

    if (!rawValue) {
      this.document = "";
      return;
    }

    // Strip non-numeric characters to get pure digits (e.g., "123.45" becomes "12345")
    let onlyNumbers = rawValue.replace(/\D/g, "");

    // Apply specific mask layout based on pure digit count
    if (onlyNumbers.length <= 11) {
      // Up to 11 digits: Format as CPF (Total length with mask: 14 chars)
      this.document = VMasker.toPattern(onlyNumbers, "999.999.999-99");
    } else {
      // More than 11 digits: Format as CNPJ. Clamp at 14 digits to prevent overflow.
      // (Total length with mask: 18 chars)
      onlyNumbers = onlyNumbers.substring(0, 14);
      this.document = VMasker.toPattern(onlyNumbers, "99.999.999/9999-99");
    }
  },

  /**
   * Dynamic input handler that evaluates pure digit length to toggle between CPF and CNPJ templates.
   *
   * @param {Event} event
   */
  handlePhoneInput(event) {
    const rawValue = event.target.value;

    if (!rawValue) {
      this.phone = "";
      return;
    }

    // Strip non-numeric characters to get pure digits (e.g., "123.45" becomes "12345")
    let onlyNumbers = rawValue.replace(/\D/g, "");

    // Apply specific mask layout based on pure digit count
    if (onlyNumbers.length <= 14) {
      this.phone = VMasker.toPattern(onlyNumbers, "(99)99999-9999");
    }
  },

  /**
   * Submit validated onboarding registration data to the public API layer.
   */
  async submitRegistration() {
    this.isLoading = true;
    this.errors = {};

    if (
      !this.document ||
      !this.company_name ||
      !this.name ||
      !this.email ||
      !this.phone ||
      !this.password
    ) {
      showAlert("Por favor, preencha todos os campos obrigatórios.", "warning");
      this.isLoading = false;
      return;
    }

    // Isolate document numbers for validation logic
    const documentNumbers = this.document.replace(/\D/g, "");
    const phoneNumbers = this.phone.replace(/\D/g, "");

    if (phoneNumbers.length !== 11) {
      showAlert("O telefone informado é inválido.", "warning");
      this.isLoading = false;
      return;
    }

    if (documentNumbers.length === 11 && !isValidCPF(documentNumbers)) {
      showAlert("O CPF informado é inválido.", "warning");
      this.isLoading = false;
      return;
    }

    if (documentNumbers.length === 14 && !isValidCNPJ(documentNumbers)) {
      showAlert("O CNPJ informado é inválido.", "warning");
      this.isLoading = false;
      return;
    }

    if (documentNumbers.length !== 11 && documentNumbers.length !== 14) {
      showAlert(
        "O documento deve ser um CPF (11 números) ou CNPJ (14 números) válido.",
        "warning",
      );
      this.isLoading = false;
      return;
    }

    if (this.password !== this.password_confirmation) {
      showAlert("As senhas informadas não coincidem.", "warning");
      this.isLoading = false;
      return;
    }

    const turnstileInput = document.querySelector(
      '[name="cf-turnstile-response"]',
    );
    const turnstileToken = turnstileInput ? turnstileInput.value : null;

    if (!turnstileToken) {
      showAlert(
        "Por favor, resolva o desafio de segurança antes de prosseguir.",
        "warning",
      );
      this.isLoading = false;
      return;
    }

    try {
      // The masked document string is fully compliant since the backend handles structural cleaning
      const response = await axios.post("/api/public/register", {
        plan_slug: this.plan_slug,
        cycle: this.cycle,
        billing_type: this.billing_type,
        document: this.document,
        company_name: this.company_name,
        name: this.name,
        email: this.email,
        phone: this.phone,
        password: this.password,
        password_confirmation: this.password_confirmation,
        "cf-turnstile-response": turnstileToken,
      });

      await showAlert(response.data.message, "success");

      this.company_name = "";
      this.name = "";
      this.email = "";
      this.phone = "";
      this.password = "";
      this.password_confirmation = "";
      this.document = "";

      window.location.href = "/client/login";
    } catch (error) {
      if (error.response && error.response.status === 422) {
        this.errors = error.response.data.errors;
        showAlert("Verifique os dados informados no formulário.", "warning");
      } else {
        handleRequestError(
          error,
          "Ocorreu uma falha ao tentar processar seu cadastro.",
        );
      }

      if (window.turnstile) {
        window.turnstile.reset();
      }
    } finally {
      this.isLoading = false;
    }
  },
});
